import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Button, Chip, Avatar, AvatarGroup,
  Tooltip, Divider, Alert,
} from '@mui/material';
import { People, Timer, Cancel, Share, QrCode2, Group, SwapHoriz, CardGiftcard } from '@mui/icons-material';
import vouchersService from '../../services/vouchersService';
import eventsService from '../../services/eventsService';
import { colors } from '../../theme/theme';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import AppSnackbar from '../../components/shared/AppSnackbar';

const POLL_INTERVAL = 15000; // 15 seconds

export default function WaitingRoomPage() {
  const { voucherId } = useParams<{ voucherId: string }>();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState<any>(null);
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'info' });
  const [confirmDlg, setConfirmDlg] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => { /* no-op */ } });

  const fetchData = useCallback(async () => {
    try {
      const vData = await vouchersService.getActive();
      const v = vData.vouchers.find((v: any) => v.voucher_id === voucherId);
      setVoucher(v);

      if (v?.event_id) {
        const e = await eventsService.getDetail(v.event_id);
        setEvent(e);
      }
    } catch (err) {
      // Polling-based load — log for observability; UI keeps last successful state.
      console.warn('[WaitingRoomPage] poll fetch failed', err);
    }
    finally { setLoading(false); }
  }, [voucherId]);

  // Initial load + polling
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCancel = () => {
    if (!voucherId) return;
    setConfirmDlg({
      open: true,
      title: 'Cancel Your Spot',
      message: 'Cancel your spot? You will receive a full refund.',
      onConfirm: async () => {
        setConfirmDlg(d => ({ ...d, open: false }));
        setCancelling(true);
        try {
          await vouchersService.cancel(voucherId);
          navigate('/vouchers');
        } catch (err) {
          console.warn('[WaitingRoomPage] cancel failed', err);
          setCancelling(false);
          setSnackbar({ open: true, message: 'Could not cancel — please try again', severity: 'error' });
        }
      },
    });
  };

  const handleWithdraw = () => {
    if (!voucherId) return;
    setConfirmDlg({
      open: true,
      title: 'Withdraw from Event',
      message: 'Withdraw from this event? Your voucher will become a floating voucher — reusable on any same-tier event within 30 days.',
      onConfirm: async () => {
        setConfirmDlg(d => ({ ...d, open: false }));
        try {
          await vouchersService.withdraw(voucherId);
          navigate('/vouchers');
        } catch (e: any) { console.warn('[WaitingRoomPage] withdraw failed', e); setSnackbar({ open: true, message: e.message || 'Failed to withdraw', severity: 'error' }); }
      },
    });
  };

  const handleShare = async () => {
    if (!event) return;
    const url = `${window.location.origin}/events/${event.id}`;
    const text = `Join me at "${event.title}" on CoffeeMates! ${event.current_count}/${event.max_capacity} spots filled.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text, url });
      } catch {
        // expected: user cancelled the native share dialog — no surfacing needed.
      }
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
    }
  };

  if (loading) return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>⏳</Typography>
      <Typography color="text.secondary">Loading your waiting room...</Typography>
    </Container>
  );

  if (!voucher) return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Typography variant="h5">Voucher not found</Typography>
      <Button sx={{ mt: 2 }} onClick={() => navigate('/vouchers')}>Go to My Vouchers</Button>
    </Container>
  );

  const isConfirmed = voucher.status === 'active' || event?.status === 'confirmed';
  const currentCount = event?.current_count || 0;
  const maxCapacity = event?.max_capacity || 6;
  const spotsLeft = Math.max(0, maxCapacity - currentCount);
  const fillPercent = Math.min(100, (currentCount / maxCapacity) * 100);
  const attendees = event?.attendees || [];

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* Main status card */}
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4, mb: 3 }}>
        <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
          {isConfirmed ? '🎉' : '⏳'}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {isConfirmed ? 'Your Table is Set!' : 'Waiting for the Table to Fill'}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {event?.title || voucher.event_title}
        </Typography>

        {isConfirmed ? (
          <>
            <Alert severity="success" sx={{ mb: 3, justifyContent: 'center' }}>
              <strong>Event confirmed!</strong> You're all set. Show your QR code at the venue.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" startIcon={<QrCode2 />}
                onClick={() => navigate(`/vouchers/${voucherId}/qr`)}>Show QR Code</Button>
              <Button variant="outlined" startIcon={<Group />}
                onClick={() => navigate('/groups/current')}>View Group</Button>
            </Box>
          </>
        ) : (
          <>
            {/* Live capacity bar */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <People sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  {currentCount}/{maxCapacity} people committed
                </Typography>
                <Typography variant="body2" sx={{ color: spotsLeft <= 2 ? colors.error.main : colors.neutral[500], fontWeight: 600 }}>
                  {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} left
                </Typography>
              </Box>
              <Box sx={{ height: 12, bgcolor: colors.neutral[100], borderRadius: 6, overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%',
                  width: `${fillPercent}%`,
                  bgcolor: fillPercent >= 100 ? colors.success.main
                    : fillPercent >= 60 ? colors.warning.main
                    : colors.primary.main,
                  borderRadius: 6,
                  transition: 'width 0.5s ease',
                }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Updates automatically every 15 seconds
              </Typography>
            </Box>

            {/* Who's coming */}
            {attendees.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Who's coming:</Typography>
                <AvatarGroup max={8} sx={{ justifyContent: 'center' }}>
                  {attendees.map((a: any, i: number) => (
                    <Tooltip key={i} title={a.first_name || 'Attendee'}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: colors.primary.main, fontSize: '0.8rem' }}>
                        {(a.first_name || '?')[0]}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              </Box>
            )}

            {/* Social proof message */}
            {currentCount >= 3 && currentCount < maxCapacity && (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                🔥 <strong>Almost there!</strong> Just {spotsLeft} more {spotsLeft === 1 ? 'person' : 'people'} needed. Share with friends to fill the table faster!
              </Alert>
            )}

            {/* Voucher info */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}>
              <Chip label={voucher.tier?.toUpperCase()} size="small" />
              <Chip label={`${voucher.amount} AZN`} size="small" />
              <Chip label={voucher.event_date} size="small" icon={<Timer sx={{ fontSize: 14 }} />} />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" startIcon={<Share />} onClick={handleShare}
                sx={{ bgcolor: colors.primary.main }}>
                Share & Fill Faster
              </Button>
              {!voucher.switch_used && (
                <Button variant="outlined" startIcon={<SwapHoriz />}
                  onClick={() => navigate(`/events?switch=${voucherId}`)}>
                  Switch Event
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {!voucher.original_event_id && (
                <Button variant="outlined" size="small" startIcon={<CardGiftcard />}
                  onClick={handleWithdraw}
                  sx={{ color: colors.warning.dark, borderColor: colors.warning.main }}>
                  Withdraw & Keep Voucher
                </Button>
              )}
              <Button variant="outlined" size="small" color="error" startIcon={<Cancel />}
                onClick={handleCancel} disabled={cancelling}>
                {cancelling ? 'Cancelling...' : 'Cancel & Full Refund'}
              </Button>
            </Box>

            {!voucher.original_event_id && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: colors.neutral[400] }}>
                Withdraw keeps your voucher for 30 days. Cancel gives you a wallet refund.
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* Event details card */}
      {event && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Event Details</Typography>
          <Typography sx={{ fontWeight: 600 }}>{event.title}</Typography>
          {event.venue_name && (
            <Typography variant="body2" color="text.secondary">📍 {event.venue_name}{event.venue_address ? ` · ${event.venue_address}` : ''}</Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            🗓 {event.scheduled_date} at {event.scheduled_time} · 90 minutes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {event.event_type === 'professional' ? '💼 Professional Discussion' :
             event.event_type === 'social' ? '🎲 Social Event' : '🎓 Master Class'}
          </Typography>
          {event.description && (
            <Typography variant="body2" sx={{ mt: 1 }}>{event.description}</Typography>
          )}
        </Paper>
      )}

      <ConfirmDialog open={confirmDlg.open} title={confirmDlg.title} message={confirmDlg.message} onConfirm={confirmDlg.onConfirm} onCancel={() => setConfirmDlg(d => ({ ...d, open: false }))} />
      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </Container>
  );
}
